import React, { useState, useEffect } from "react";
import {
  ShoppingBag, DollarSign, Package, TrendingUp, CheckSquare, Square,
  Plus, Edit2, Trash2, Eye, Search, Filter, Grid, List, Tag, BarChart3
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function StoreManagementEnhanced() {
  const [activeTab, setActiveTab] = useState("videos"); // videos | bundles | revenue | pricing
  const [videos, setVideos] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEnabled, setFilterEnabled] = useState("all"); // all | enabled | disabled
  const [viewMode, setViewMode] = useState("grid");

  // Modals
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [bundleForm, setBundleForm] = useState({
    name: "",
    description: "",
    price: 0,
    video_ids: [],
    bundle_type: "manual"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [videosRes, statsRes, bundlesRes, templatesRes, revenueRes] = await Promise.all([
        api.get("/api/catalog"),
        api.get("/api/admin/store/stats"),
        api.get("/api/admin/store/bundles"),
        api.get("/api/admin/store/pricing-templates"),
        api.get("/api/admin/store/revenue")
      ]);
      
      setVideos(videosRes.data || []);
      setStats(statsRes.data);
      setBundles(bundlesRes.data.bundles || []);
      setTemplates(templatesRes.data.templates || []);
      setRevenue(revenueRes.data);
    } catch (error) {
      toast.error("Failed to load store data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Bulk Actions
  const bulkEnable = async (enabled) => {
    if (selectedVideos.size === 0) {
      toast.error("No videos selected");
      return;
    }

    try {
      await api.post("/api/admin/store/bulk/enable", {
        video_ids: Array.from(selectedVideos),
        store_enabled: enabled
      });
      
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${selectedVideos.size} video(s)`);
      setSelectedVideos(new Set());
      loadData();
    } catch (error) {
      toast.error("Bulk action failed");
    }
  };

  const bulkSetPrice = async (directPrice, ppvPrice) => {
    if (selectedVideos.size === 0) {
      toast.error("No videos selected");
      return;
    }

    try {
      await api.post("/api/admin/store/bulk/pricing", {
        video_ids: Array.from(selectedVideos),
        store_direct_sale_price: directPrice,
        store_ppv_price: ppvPrice
      });
      
      toast.success(`Updated pricing for ${selectedVideos.size} video(s)`);
      setSelectedVideos(new Set());
      loadData();
    } catch (error) {
      toast.error("Pricing update failed");
    }
  };

  // Bundle Management
  const createBundle = async () => {
    if (!bundleForm.name || bundleForm.video_ids.length === 0) {
      toast.error("Bundle name and videos required");
      return;
    }

    try {
      await api.post("/api/admin/store/bundles", {
        name: bundleForm.name,
        description: bundleForm.description,
        video_ids: bundleForm.video_ids,
        price: bundleForm.price,
        bundle_type: bundleForm.bundle_type,
        active: true
      });
      
      toast.success("Bundle created");
      setShowBundleModal(false);
      setBundleForm({ name: "", description: "", price: 0, video_ids: [], bundle_type: "manual" });
      loadData();
    } catch (error) {
      toast.error("Failed to create bundle");
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!window.confirm("Delete this bundle?")) return;

    try {
      await api.delete(`/api/admin/store/bundles/${bundleId}`);
      toast.success("Bundle deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete bundle");
    }
  };

  // Selection
  const toggleSelectVideo = (videoId) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      newSelected.add(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedVideos.size === filteredVideos.length && filteredVideos.length > 0) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(filteredVideos.map(v => v.id)));
    }
  };

  // Filtering
  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    if (filterEnabled === "all") return true;
    if (filterEnabled === "enabled") return video.store_enabled === true;
    if (filterEnabled === "disabled") return video.store_enabled !== true;
    
    return true;
  });

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-400">Loading store management...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Store Management</h1>
          <p className="text-zinc-400">Pricing, bundles, and revenue tracking</p>
        </div>
        <ShoppingBag size={40} className="text-purple-400" />
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <ShoppingBag size={20} className="text-blue-400" />
              <span className="text-sm text-zinc-400">Active Videos</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.enabled_videos}</div>
            <div className="text-xs text-zinc-500 mt-1">of {stats.total_videos} total</div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign size={20} className="text-green-400" />
              <span className="text-sm text-zinc-400">Avg Price</span>
            </div>
            <div className="text-3xl font-bold text-white">${stats.average_direct_price}</div>
            <div className="text-xs text-zinc-500 mt-1">Direct sale</div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package size={20} className="text-purple-400" />
              <span className="text-sm text-zinc-400">Bundles</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.active_bundles}</div>
            <div className="text-xs text-zinc-500 mt-1">of {stats.total_bundles} total</div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={20} className="text-yellow-400" />
              <span className="text-sm text-zinc-400">Revenue</span>
            </div>
            <div className="text-3xl font-bold text-white">${revenue?.total_revenue || 0}</div>
            <div className="text-xs text-zinc-500 mt-1">All time</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        {[
          { id: "videos", label: "Videos", icon: ShoppingBag },
          { id: "bundles", label: "Bundles", icon: Package },
          { id: "revenue", label: "Revenue", icon: TrendingUp },
          { id: "pricing", label: "Pricing", icon: Tag }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition ${
                activeTab === tab.id
                  ? "border-purple-500 text-white"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "videos" && (
        <div>
          {/* Filters & Bulk Actions */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  <Search size={14} className="inline mr-1" />
                  Search
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search videos..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  <Filter size={14} className="inline mr-1" />
                  Filter
                </label>
                <select
                  value={filterEnabled}
                  onChange={(e) => setFilterEnabled(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="all">All Videos</option>
                  <option value="enabled">Enabled Only</option>
                  <option value="disabled">Disabled Only</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">View Mode</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`flex-1 px-4 py-2 rounded-lg transition ${
                      viewMode === "grid" 
                        ? "bg-purple-600 text-white" 
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    <Grid size={16} className="inline mr-2" />
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`flex-1 px-4 py-2 rounded-lg transition ${
                      viewMode === "list" 
                        ? "bg-purple-600 text-white" 
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    <List size={16} className="inline mr-2" />
                    List
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedVideos.size > 0 && (
              <div className="flex items-center gap-4 pt-4 border-t border-zinc-800">
                <span className="text-sm text-zinc-400">
                  {selectedVideos.size} selected
                </span>
                
                <button
                  onClick={() => bulkEnable(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition"
                >
                  Enable
                </button>
                
                <button
                  onClick={() => bulkEnable(false)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition"
                >
                  Disable
                </button>
                
                <button
                  onClick={() => {
                    const price = prompt("Set direct sale price:");
                    if (price) bulkSetPrice(parseFloat(price), null);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition"
                >
                  Set Price
                </button>
                
                <button
                  onClick={() => setSelectedVideos(new Set())}
                  className="ml-auto px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm transition"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Videos Grid */}
          {viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredVideos.map((video) => (
                <div
                  key={video.id}
                  className={`bg-zinc-900 border rounded-xl overflow-hidden transition ${
                    selectedVideos.has(video.id) 
                      ? "border-purple-500 ring-2 ring-purple-500/50" 
                      : "border-zinc-800"
                  }`}
                >
                  <div className="relative">
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.title} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 bg-zinc-800 flex items-center justify-center">
                        <ShoppingBag size={32} className="text-zinc-600" />
                      </div>
                    )}
                    
                    <button
                      onClick={() => toggleSelectVideo(video.id)}
                      className="absolute top-2 left-2 p-2 bg-black/50 rounded-lg hover:bg-black/70 transition"
                    >
                      {selectedVideos.has(video.id) ? (
                        <CheckSquare size={16} className="text-purple-400" />
                      ) : (
                        <Square size={16} className="text-white" />
                      )}
                    </button>
                    
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-semibold ${
                      video.store_enabled 
                        ? "bg-green-600 text-white" 
                        : "bg-zinc-700 text-zinc-400"
                    }`}>
                      {video.store_enabled ? "Enabled" : "Disabled"}
                    </div>
                  </div>
                  
                  <div className="p-3">
                    <h3 className="font-semibold text-white text-sm mb-1 truncate">
                      {video.title}
                    </h3>
                    <div className="text-lg font-bold text-green-400 mb-2">
                      ${video.store_direct_sale_price || 0}
                    </div>
                    <div className="text-xs text-zinc-500">
                      PPV: ${video.store_ppv_price || 0}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // List View
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-800">
                  <tr>
                    <th className="px-6 py-4 text-left">
                      <button onClick={toggleSelectAll} className="text-zinc-400 hover:text-white">
                        {selectedVideos.size === filteredVideos.length && filteredVideos.length > 0 ? (
                          <CheckSquare size={18} />
                        ) : (
                          <Square size={18} />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Video</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">PPV</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {filteredVideos.map((video) => (
                    <tr key={video.id} className="hover:bg-zinc-800/50 transition">
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleSelectVideo(video.id)}
                          className="text-zinc-400 hover:text-white"
                        >
                          {selectedVideos.has(video.id) ? (
                            <CheckSquare size={18} className="text-purple-400" />
                          ) : (
                            <Square size={18} />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-white">{video.title}</div>
                        <div className="text-sm text-zinc-400">{video.cast || "No cast"}</div>
                      </td>
                      <td className="px-6 py-4 text-green-400 font-bold">
                        ${video.store_direct_sale_price || 0}
                      </td>
                      <td className="px-6 py-4 text-zinc-400">
                        ${video.store_ppv_price || 0}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          video.store_enabled 
                            ? "bg-green-600/20 text-green-400" 
                            : "bg-zinc-700 text-zinc-400"
                        }`}>
                          {video.store_enabled ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "bundles" && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Video Bundles</h2>
            <button
              onClick={() => setShowBundleModal(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition"
            >
              <Plus size={16} />
              Create Bundle
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bundles.map((bundle) => (
              <div key={bundle.bundle_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-white mb-1">{bundle.name}</h3>
                    <p className="text-sm text-zinc-400">{bundle.description}</p>
                  </div>
                  <button
                    onClick={() => deleteBundle(bundle.bundle_id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    ${bundle.price}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {bundle.video_ids?.length || 0} videos
                  </div>
                </div>
                
                <div className={`px-3 py-1 rounded-full text-xs font-semibold inline-block ${
                  bundle.active 
                    ? "bg-green-600/20 text-green-400" 
                    : "bg-zinc-700 text-zinc-400"
                }`}>
                  {bundle.active ? "Active" : "Inactive"}
                </div>
              </div>
            ))}
            
            {bundles.length === 0 && (
              <div className="col-span-full text-center py-12 text-zinc-400">
                No bundles yet. Create one to get started!
              </div>
            )}
          </div>

          {/* Bundle Modal */}
          {showBundleModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-2xl w-full mx-4">
                <h3 className="text-xl font-bold text-white mb-4">Create Bundle</h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Bundle Name</label>
                    <input
                      type="text"
                      value={bundleForm.name}
                      onChange={(e) => setBundleForm({...bundleForm, name: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="e.g., Summer Collection"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Description</label>
                    <textarea
                      value={bundleForm.description}
                      onChange={(e) => setBundleForm({...bundleForm, description: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      rows="3"
                      placeholder="Bundle description..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Price</label>
                    <input
                      type="number"
                      value={bundleForm.price}
                      onChange={(e) => setBundleForm({...bundleForm, price: parseFloat(e.target.value)})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="29.99"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Videos (Select from enabled videos)
                    </label>
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 max-h-48 overflow-y-auto">
                      {videos.filter(v => v.store_enabled).map((video) => (
                        <label key={video.id} className="flex items-center gap-2 py-2 hover:bg-zinc-700/50 px-2 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bundleForm.video_ids.includes(video.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBundleForm({
                                  ...bundleForm,
                                  video_ids: [...bundleForm.video_ids, video.id]
                                });
                              } else {
                                setBundleForm({
                                  ...bundleForm,
                                  video_ids: bundleForm.video_ids.filter(id => id !== video.id)
                                });
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-white text-sm">{video.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={createBundle}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
                  >
                    Create Bundle
                  </button>
                  <button
                    onClick={() => setShowBundleModal(false)}
                    className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "revenue" && (
        <div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Revenue Dashboard</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-sm text-zinc-400 mb-1">Today</div>
                <div className="text-2xl font-bold text-white">${revenue?.today_revenue || 0}</div>
              </div>
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-sm text-zinc-400 mb-1">This Week</div>
                <div className="text-2xl font-bold text-white">${revenue?.week_revenue || 0}</div>
              </div>
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-sm text-zinc-400 mb-1">This Month</div>
                <div className="text-2xl font-bold text-white">${revenue?.month_revenue || 0}</div>
              </div>
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-sm text-zinc-400 mb-1">All Time</div>
                <div className="text-2xl font-bold text-green-400">${revenue?.total_revenue || 0}</div>
              </div>
            </div>
            
            <div className="text-center py-12 text-zinc-400">
              <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
              <p>Revenue tracking integration pending</p>
              <p className="text-sm mt-2">Connect your payment processor to see live data</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "pricing" && (
        <div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">Pricing Templates</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-zinc-800 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2">Standard</h3>
                <div className="text-2xl font-bold text-green-400 mb-1">$19.99</div>
                <div className="text-sm text-zinc-400 mb-4">Direct Sale: $19.99 | PPV: $4.99</div>
                <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition">
                  Apply to Selected
                </button>
              </div>
              
              <div className="bg-zinc-800 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2">Premium</h3>
                <div className="text-2xl font-bold text-green-400 mb-1">$29.99</div>
                <div className="text-sm text-zinc-400 mb-4">Direct Sale: $29.99 | PPV: $7.99</div>
                <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition">
                  Apply to Selected
                </button>
              </div>
              
              <div className="bg-zinc-800 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2">Exclusive</h3>
                <div className="text-2xl font-bold text-green-400 mb-1">$49.99</div>
                <div className="text-sm text-zinc-400 mb-4">Direct Sale: $49.99 | PPV: $12.99</div>
                <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition">
                  Apply to Selected
                </button>
              </div>
            </div>
            
            <button className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-2 transition">
              <Plus size={16} />
              Create Custom Template
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
